<template>
  <span class="collaborator-preview-chip" @click.stop>
    <SrpDropdown placement="bottom-start" :offset="4" exclusiveGroup="location-preview-chip">
      <template #clickableElement="{ isDropdownVisible }">
        <button
          type="button"
          class="collaborator-preview-chip__trigger"
          :class="{ 'collaborator-preview-chip__trigger--open': isDropdownVisible }"
          :aria-expanded="isDropdownVisible"
          aria-haspopup="dialog"
        >
          <IconEmbedded name="user-plus_2-5" :size="13" color="#667085" />
          <span>@{{ displayHandle }}</span>
        </button>
      </template>
      <template #dropdown>
        <div class="collaborator-preview-chip__panel" role="dialog" aria-label="Instagram Collaborator">
          <div class="collaborator-preview-chip__heading">Instagram Collaborator</div>
          <a class="collaborator-preview-chip__row" :href="profileHref" target="_blank" rel="noopener noreferrer">
            <span class="collaborator-preview-chip__label">@{{ displayHandle }} profile</span>
            <IconEmbedded name="external-link_2-5" :size="14" color="#667085" />
          </a>
        </div>
      </template>
    </SrpDropdown>
  </span>
</template>

<script setup lang="ts">
import { computed } from "vue";
import SrpDropdown from "@components/ui/SrpDropdown.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import { buildInstagramProfileUrl } from "@logic/SocialFieldInputCleaner";

const props = defineProps<{
  handle: string;
}>();

const displayHandle = computed(() => props.handle.trim().replace(/^@/, ""));
const profileHref = computed(() => buildInstagramProfileUrl(displayHandle.value));
</script>

<style scoped lang="scss">
.collaborator-preview-chip {
  display: inline-flex;
  max-width: 100%;
  min-width: 0;

  :deep(.dropdown-wrapper),
  :deep(.dropdown-wrapper__clickable-element) {
    max-width: 100%;
    min-width: 0;
  }

  &__trigger {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    max-width: 100%;
    padding: 4px 8px 4px 6px;
    border: 1px solid #d0d5dd;
    border-radius: 6px;
    background: #fff;
    color: #344054;
    font: 12px/16px sans-serif;
    cursor: pointer;
    transition:
      border-color 0.12s ease,
      background-color 0.12s ease,
      box-shadow 0.12s ease;

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &:hover,
    &:focus-visible,
    &--open {
      border-color: #98a2b3;
      background: #f2f4f7;
      box-shadow: 0 1px 2px rgba(16, 24, 40, 0.08);
    }

    &:focus-visible {
      outline: 2px solid #98a2b3;
      outline-offset: 1px;
    }
  }

  &__panel {
    display: grid;
    gap: 6px;
    min-width: 220px;
    max-width: min(420px, calc(100vw - 24px));
    padding: 10px 12px;
    border: 1px solid #d0d5dd;
    border-radius: 5px;
  }

  &__heading {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #344054;
    font-size: 15px;
    line-height: 20px;
    font-weight: 600;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    color: #344054;
    font: 13px/18px sans-serif;
    text-decoration: none;

    &:hover .collaborator-preview-chip__label {
      text-decoration: underline;
    }
  }

  &__label {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
